import { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { PieChart, Pie } from "recharts";
import heartLoading from "../../../assests/heartLoading.gif";
import { Tooltip as MuiTooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
const ROLE_AVATAR_COLOR = {
  develop: { bg: "#e0e7ff", text: "#bb1ccc" },
  support: { bg: "#d1fae5", text: "#065f46" },
  reader: { bg: "#f3f4f6", text: "#6b7280" },
};

function MemberPanel({ role, onClose }) {
  if (!role) return null;
  const titleMap = { develop: "Develop group", support: "Support group" };
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 40, backgroundColor: "rgba(0,0,0,0.15)" }} />
      <div style={{ position: "fixed", top: 0, right: 0, width: "320px", height: "100vh", backgroundColor: "#ffffff", boxShadow: "-4px 0 24px rgba(0,0,0,0.12)", zIndex: 50, display: "flex", flexDirection: "column", fontFamily: "sans-serif" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #f3f4f6", flexShrink: 0 }}>
          <span className="text-[12px] font-semibold text-[#111827]">{titleMap[role.role] ?? `${role.role} group`}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "12px", color: "#9ca3af", lineHeight: 1, padding: "2px 6px" }}>✕</button>
        </div>
        <div style={{ padding: "12px 20px 8px", flexShrink: 0 }}>
          <MuiTooltip
            title="The count and names can vary, since some members' names are not available."
            arrow
            placement="top"
            PopperProps={{
              style: { zIndex: 9999 }
            }}
          >  <span style={{ display: "inline-block", fontSize: "10px", backgroundColor: "#eef2ff", color: "#bb1ccc", border: "1px solid #e0e7ff", borderRadius: "999px", padding: "3px 12px", cursor: "pointer" }}>
              {role.member_count} members total
            </span>
          </MuiTooltip>
          <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: "8px", fontFamily: "monospace", letterSpacing: "0.02em" }}>{role.group_name}</div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 20px 16px", display: "flex", flexDirection: "column", gap: "8px" }}>
          {role.sample_members.length === 0 ? (
            <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: "16px", textAlign: "center" }}>No members to display</div>
          ) : (
            role.sample_members.map((member, i) => {
              const color = ROLE_AVATAR_COLOR[role.role] ?? ROLE_AVATAR_COLOR.reader;
              const displayName = member.email.split("@")[0].replace(/\./g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px", border: "1px solid #f3f4f6", borderRadius: "8px", padding: "10px 12px", backgroundColor: "#ffffff" }}>
                  <div style={{ width: "20px", height: "20px", minWidth: "20px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "700", backgroundColor: color.bg, color: color.text }}>{member.initials}</div>
                  <div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: "10px", fontWeight: "500", color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayName}</span>
                    <span style={{ fontSize: "10px", color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{member.email}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}

function SizeBar({ active, vacuumable, total }) {
  const activeW = total > 0 ? (active / total) * 100 : 0;
  const vacW = total > 0 ? (vacuumable / total) * 100 : 0;
  return (
    <div className="flex h-1.5 w-full rounded overflow-hidden mt-1">
      <div style={{ width: `${activeW}%`, backgroundColor: "#6366f1" }} />
      <div style={{ width: `${vacW}%`, backgroundColor: "#f59e0b" }} />
    </div>
  );
}

// Add this custom tick component above your main component
const CustomXAxisTick = ({ x, y, payload }) => {
  const label = payload.value.length > 12 ? payload.value.slice(0, 12) + "..." : payload.value;
  return (
    <text x={x} y={y + 8} textAnchor="middle" fontSize={9} fill="#9ca3af">
      {label}
    </text>
  );
};

function formatDate(dateStr) {
  if (!dateStr) return "–";
  return dateStr.split(" ")[0];
}

// ── Reusable empty state component ──
function EmptyState({ icon, title, subtitle, height = "h-48" }) {
  const icons = {
    bar: (
      <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    table: (
      <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 6h18M3 14h18M3 18h18" />
      </svg>
    ),
    pie: (
      <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
      </svg>
    ),
  };
  return (
    <div className={`flex flex-col items-center justify-center ${height} gap-2`}>
      {icons[icon]}
      <p className="text-sm font-medium text-gray-400">{title}</p>
      {subtitle && <p className="text-xs text-gray-300 text-center">{subtitle}</p>}
    </div>
  );
}

export default function StorageDetails({ productId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [columnSearch, setColumnSearch] = useState({
    name: "", registered: "", status: "", last_used_date: "",
    total_size_gb: "", active_gb: "", vacuumable_gb: "", usage_events: "",
  });

  const fetchData = () => {
    if (!productId) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${process.env.REACT_APP_API_URL}/storage-dashboard/${productId}`)
      .then((res) => setData(res.data))
      .catch(() => setError("No configuration found for this Product"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [productId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center space-y-3">
          <img src={heartLoading} alt="Loading..."style={{ width : 200}}/>
          <span className="text-gray-700 text-[20px] text-center">Loading storage details...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <svg className="w-14 h-14 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
        <p className="text-gray-500 text-sm">{error}</p>

      </div>
    );
  }

  if (!data) return null;

  const { configuration, top_stats, roles, table_summary, charts, tables } = data;
  const { storage_per_table, usage_events_per_table, registration_status } = charts;

  const hasStorageData = storage_per_table?.length > 0;
  const hasUsageData = usage_events_per_table?.length > 0;
  const hasRegistrationData = registration_status?.length > 0 && registration_status.some(r => r.value > 0);
  const filteredTables = (tables ?? []).filter(row =>
    String(row.name ?? "").toLowerCase().includes(columnSearch.name.toLowerCase()) &&
    String(row.registered ? "yes" : "no").includes(columnSearch.registered.toLowerCase()) &&
    String(row.status ?? "").toLowerCase().includes(columnSearch.status.toLowerCase()) &&
    String(formatDate(row.last_used_date)).includes(columnSearch.last_used_date) &&
    String(row.total_size_gb ?? "").includes(columnSearch.total_size_gb) &&
    String(row.active_gb ?? "").includes(columnSearch.active_gb) &&
    String(row.vacuumable_gb ?? "").includes(columnSearch.vacuumable_gb) &&
    String(row.usage_events ?? "").includes(columnSearch.usage_events)
  );
  const hasTables = filteredTables.length > 0;
  const pieData = (registration_status ?? []).map((item, i) => ({
    ...item,
    color: item.color ?? (i === 0 ? "#14B8A6" : "#e5e7eb"),
  }));

  const subColorMap = {
    "ACTIVE SIZE": "text-indigo-200",
    "VACUUMABLE": "text-yellow-300",
    "USAGE EVENTS": "text-indigo-200",
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800 ml-2 mt-4 mr-2">
      <MemberPanel role={selectedRole} onClose={() => setSelectedRole(null)} />

      {/* ── Top Stats ── */}
      <div className="grid grid-cols-4 gap-4 px-6 py-4">
        {(top_stats ?? []).map((s, i) => (
          <div key={i} className="flex flex-wrap flex-col items-center justify-center   border border-gray-200 rounded-[10px] bg-white px-[16px] py-[12px] ">
            <div className="text-[11px] font-semibold tracking-widest text-gray-400 mb-1">{s.label}</div>
            <div className="text-[28px] font-bold text-gray-900 leading-none">{s.value}</div>
            <div className="text-[13px] mt-[6px] font-medium text-[#1F2937]">
              {s.sub_text === "this period" ? "This period" : s.sub_text}
            </div>          </div>
        ))}
      </div>

      <div className="px-6 py-4">

        {/* ── Configuration / Roles ── */}
        <div className="mb-5">
          {/* <div className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-2">
            Configuration · {configuration}
          </div> */}
          <div className="grid grid-cols-2 gap-3">
            {(roles ?? []).filter((role) => role.role !== "reader").map((role, i) => {
              const styleMap = {
                develop: { wrapper: "rounded border border-blue-200 bg-blue-50 p-3", label: "text-xs font-bold text-blue-700 uppercase tracking-wider", badge: "text-xs bg-blue-100 text-blue-600 border border-blue-300 rounded px-2 py-0.5", count: "text-4xl font-bold text-blue-800", name: "text-xs text-blue-600 font-semibold mt-1", sub: "text-xs text-blue-400 mt-0.5" },
                support: { wrapper: "rounded border border-[#14B8A6] bg-green-50 p-3", label: "text-xs font-bold text-[#14B8A6] uppercase tracking-wider", badge: "text-xs bg-green-100 text-[#14B8A6] border border-[#14B8A6] rounded px-2 py-0.5", count: "text-4xl font-bold text-[#14B8A6]", name: "text-xs text-[#14B8A6] font-semibold mt-1", sub: "text-xs text-[#14B8A6] mt-0.5" },
              };
              const s = styleMap[role.role] ?? styleMap.support;
              return (
                <div key={i} className={`${s.wrapper} cursor-pointer hover:shadow-md transition-shadow`} onClick={() => setSelectedRole(role)}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={s.label}>{role.role}</span>
                    <span className={s.badge}>{role.scope}</span>
                  </div>
                  <div className={s.count}>{role.member_count}</div>
                  <div className={s.name}>{role.group_name}</div>
                  <div className={s.sub}>members · click to view</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Table Summary ── */}
        <div className="mb-4">
          <div className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-3 ml-2">PULSE REGISTER</div>
          <div className="grid grid-cols-5 mb-4 ml-2">
            {[
              { value: table_summary?.total_tables, label: "TOTAL TABLES", color: "text-gray-800" },
              { value: table_summary?.registered_count, label: "REGISTERED", color: "text-indigo-500" },
              { value: table_summary?.active_count, label: "ACTIVE", color: "text-indigo-500" },
              { value: table_summary?.inactive_count, label: "INACTIVE", color: "text-gray-800" },
              { value: table_summary?.usage_events, label: "USAGE EVENTS", color: "text-yellow-500" },
            ].map((s, i) => (
              <div key={i} className="flex flex-col">
                <span className={`text-3xl font-bold ${s.color}`}>{s.value ?? 0}</span>
                <span className="text-xs text-gray-400 font-semibold tracking-wider mt-0.5">{s.label}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-1">

            {/* LEFT — Storage per table */}
            <div className="w-1/2 bg-white rounded border border-gray-200 p-4">
              <div className="text-sm font-semibold text-gray-600 mb-2">Storage per table</div>

              {hasStorageData ? (
                <>
                  {/* Legend — only shown when there is data */}
                  <div className="flex gap-5 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3.5 h-3.5 rounded-sm bg-indigo-500" />
                      <span className="text-xs text-gray-500">Active</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3.5 h-3.5 rounded-sm bg-[#818CFA]" />
                      <span className="text-xs text-gray-500">Vacuumable</span>
                    </div>
                  </div>
                  <div style={{ maxHeight: 300, overflowY: "auto" }}>
                    <div style={{ height: storage_per_table.length * 60 + 60 }}>
                      <ResponsiveContainer width="100%" height={storage_per_table.length * 60 + 60}>
                        <BarChart data={storage_per_table} layout="vertical" margin={{ top: 2, right: 24, left: 0, bottom: 6 }} barCategoryGap="20%" barSize={18}>
                          <XAxis type="number" tickFormatter={(v) => `${v} GB`} tick={{ fontSize: 11, fill: "#9ca3af" }} domain={[0, "dataMax + 1"]} tickCount={6} axisLine={false} tickLine={false} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#6b7280" }} axisLine={false} tickLine={false} width={90} interval={0} tickFormatter={(v) => v.length > 16 ? `${v.slice(0, 16)}…` : v} />
                          <Tooltip formatter={(value, name) => [`${value} GB`, name === "active_gb" ? "Active" : "Vacuumable"]} contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                          <Bar dataKey="active_gb" stackId="a" fill="#6366f1" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="vacuumable_gb" stackId="a" fill="#818CFA" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </>
              ) : (
                <EmptyState icon="bar" title="No storage data available" subtitle="Table storage breakdown will appear here" height="h-52" />
              )}
            </div>

            {/* RIGHT — Usage events + Registration stacked */}
            <div className="w-1/2 flex flex-col gap-4">

              <div className="bg-white rounded border border-gray-200 p-3">
                <div className="text-xs font-semibold text-gray-500 mb-2">Usage events per table</div>
                {hasUsageData ? (
                  <div className="overflow-x-auto">
                    <BarChart
                      width={Math.max(usage_events_per_table.length * 100, 400)}
                      data={usage_events_per_table.map(d => ({
                        ...d,
                        displayName: d.name.length > 12 ? d.name.slice(0, 12) + "..." : d.name
                      }))}
                      height={240}
                      margin={{ top: 8, right: 16, left: 0, bottom: 40 }}
                      barCategoryGap="40%"
                      barSize={28}
                    >
                      <XAxis
                        dataKey="displayName"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        height={40}
                        tick={<CustomXAxisTick />}
                      />
                      <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={false} tickLine={false} domain={[0, "auto"]} tickCount={4} width={30} />
                      <Tooltip
                        contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
                        cursor={{ fill: "rgba(0,0,0,0.04)" }}
                        formatter={(value) => [value, "events"]}
                        labelFormatter={(label, payload) => payload?.[0]?.payload?.name || label}
                      />
                      <Bar dataKey="events" fill="#6366f1" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </div>
                ) : (
                  <EmptyState icon="bar" title="No usage events recorded" subtitle="Events will appear once tables are accessed" height="h-36" />
                )}
              </div>

              {/* Registration Status */}
              <div className="bg-white rounded border border-gray-200 p-3">
                <div className="text-xs font-semibold text-gray-500 mb-2">Registration status</div>
                {hasRegistrationData ? (
                  <div className="flex items-center gap-4">
                    {/* FIX 3: added <Tooltip> inside PieChart */}
                    <PieChart width={80} height={70}>
                      <Pie data={pieData} cx={35} cy={33} innerRadius={22} outerRadius={34} dataKey="value" startAngle={90} endAngle={-270} stroke="none">
                        {pieData.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                      </Pie>
                      <Tooltip formatter={(value, name) => [value, name]} contentStyle={{ fontSize: 11, borderRadius: 6, border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }} />
                    </PieChart>
                    <div className="flex flex-col gap-1">
                      {pieData.map((entry, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: entry.color }} />
                          <span className="text-xs text-gray-600">{entry.name} ({entry.value})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <EmptyState icon="pie" title="No registration data" subtitle="Register tables to see status breakdown" height="h-24" />
                )}
              </div>

            </div>
          </div>
        </div>

        {/* ── Tables Detail ── */}
        <div>
          <div className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-2">
            Tables · Storage &amp; Usage Detail
          </div>
          <div className="bg-white rounded border border-gray-200 overflow-x-auto h-[300px] overflow-y-auto" >
            <table className="w-full text-xs">
              <thead>
                <tr className=" border-gray-100">
                  <th className="text-left px-4 py-2.5  text-indigo-500 font-semibold tracking-wider uppercase">Table Name</th>
                  <th className="text-left px-3 py-2.5  text-indigo-500 font-semibold tracking-wider uppercase">Registered</th>
                  <th className="text-left px-3 py-2.5  text-indigo-500 font-semibold tracking-wider uppercase">Status</th>
                  <th className="text-left px-3 py-2.5  text-indigo-500 font-semibold tracking-wider uppercase">Last Used</th>
                  <th className="text-left px-3 py-2.5 text-indigo-500 font-semibold whitespace-nowrap tracking-wider uppercase">Total Size</th>
                  <th className="text-left px-3 py-2.5 text-indigo-400 font-semibold tracking-wider uppercase">Active</th>
                  <th className="text-left px-3 py-2.5 text-yellow-500 font-semibold tracking-wider uppercase">Vacuumable</th>
                  <th className="text-left px-3 py-2.5 text-indigo-500 font-semibold tracking-wider uppercase">Events</th>
                </tr>
                <tr className="border-gray-100">
                  <th className="text-left">
                    <div className="relative ml-[2px]">
                      {!columnSearch.name && <SearchIcon className="absolute left-1.5 top-1/2 -translate-y-1/2 pointer-events-none !text-[14px] !text-[#6366f1]" />}
                      <input type="text" value={columnSearch.name} onChange={e => setColumnSearch(prev => ({ ...prev, name: e.target.value }))} className="w-full text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:border-indigo-400 font-normal" />
                    </div>
                  </th>
                  <th className="px-3 py-1 text-center">
                    <div className="relative">
                      {!columnSearch.registered && <SearchIcon className="absolute left-1.5 top-1/2 -translate-y-1/2 pointer-events-none !text-[14px] !text-[#6366f1]" />}
                      <input type="text" value={columnSearch.registered} onChange={e => setColumnSearch(prev => ({ ...prev, registered: e.target.value }))} className="w-full text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:border-indigo-400 font-normal" />
                    </div>
                  </th>
                  <th className="px-3 py-1 text-center">
                    <div className="relative">
                      {!columnSearch.status && <SearchIcon className="absolute left-1.5 top-1/2 -translate-y-1/2 pointer-events-none !text-[14px] !text-[#6366f1]" />}
                      <input type="text" value={columnSearch.status} onChange={e => setColumnSearch(prev => ({ ...prev, status: e.target.value }))} className="w-full text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:border-indigo-400 font-normal" />
                    </div>
                  </th>
                  <th className="px-3 py-1 text-center">
                    <div className="relative">
                      {!columnSearch.last_used_date && <SearchIcon className="absolute left-1.5 top-1/2 -translate-y-1/2 pointer-events-none !text-[14px] !text-[#6366f1]" />}
                      <input type="text" value={columnSearch.last_used_date} onChange={e => setColumnSearch(prev => ({ ...prev, last_used_date: e.target.value }))} className="w-full text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:border-indigo-400 font-normal" />
                    </div>
                  </th>
                  <th className="px-3 py-1 text-right">
                    <div className="relative">
                      {!columnSearch.total_size_gb && <SearchIcon className="absolute left-1.5 top-1/2 -translate-y-1/2 pointer-events-none !text-[14px] !text-[#6366f1]" />}
                      <input type="text" value={columnSearch.total_size_gb} onChange={e => setColumnSearch(prev => ({ ...prev, total_size_gb: e.target.value }))} className="w-full text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:border-indigo-400 font-normal" />
                    </div>
                  </th>
                  <th className="px-3 py-1 text-right">
                    <div className="relative">
                      {!columnSearch.active_gb && <SearchIcon className="absolute left-1.5 top-1/2 -translate-y-1/2 pointer-events-none !text-[14px] !text-[#6366f1]" />}
                      <input type="text" value={columnSearch.active_gb} onChange={e => setColumnSearch(prev => ({ ...prev, active_gb: e.target.value }))} className="w-full text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:border-indigo-400 font-normal" />
                    </div>
                  </th>
                  <th className="px-3 py-1 text-right">
                    <div className="relative">
                      {!columnSearch.vacuumable_gb && <SearchIcon className="absolute left-1.5 top-1/2 -translate-y-1/2 pointer-events-none !text-[14px] !text-[#6366f1]" />}
                      <input type="text" value={columnSearch.vacuumable_gb} onChange={e => setColumnSearch(prev => ({ ...prev, vacuumable_gb: e.target.value }))} className="w-full text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:border-indigo-400 font-normal" />
                    </div>
                  </th>
                  <th className="px-3 py-1 text-right">
                    <div className="relative">
                      {!columnSearch.usage_events && <SearchIcon className="absolute left-1.5 top-1/2 -translate-y-1/2 pointer-events-none !text-[14px] !text-[#6366f1]" />}
                      <input type="text" value={columnSearch.usage_events} onChange={e => setColumnSearch(prev => ({ ...prev, usage_events: e.target.value }))} className="w-full text-xs border border-gray-200 rounded px-2 py-1 outline-none focus:border-indigo-400 font-normal" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {hasTables ? (
                  filteredTables.map((row, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-800">{row.name}</div>
                        <div className="text-gray-400 text-xs mt-0.5">{row.full_path}</div>
                      </td>
                      <td className="px-3 py-3 text-left">
                        {row.registered ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-green-600 text-xs font-medium">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-500 text-xs font-medium">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 12 12"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
                            no
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-left">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${row.status === "active" ? "bg-indigo-100 text-indigo-600" : "bg-gray-100 text-gray-500"}`}>
                          {row.status}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-left whitespace-nowrap text-gray-500">{formatDate(row.last_used_date)}</td>
                      <td className="px-3 py-3 text-left">
                        <div className="font-semibold whitespace-nowrap text-gray-800">{row.total_size_gb.toFixed(2)} GB</div>
                        <SizeBar active={row.active_gb} vacuumable={row.vacuumable_gb} total={row.total_size_gb} />
                      </td>
                      <td className="px-3 py-3 text-left whitespace-nowrap text-gray-600">{row.active_gb} GB</td>
                      <td className="px-3 py-3 text-left whitespace-nowrap font-semibold text-yellow-500">{row.vacuumable_gb.toFixed(2)} GB</td>
                      <td className="px-3 py-3 whitespace-nowrap text-left"><span className="text-gray-700 font-medium">{row.usage_events}</span></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="px-4 py-16 text-left">
                      <div className="flex flex-col items-center gap-3">
                        <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 6h18M3 14h18M3 18h18" />
                        </svg>
                        <p className="text-sm font-semibold text-gray-400">No tables found</p>
                        <p className="text-xs text-gray-300">Storage &amp; usage data will appear here once available</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}